import React, { memo, useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'

import { useActionless, useAppSelector } from 'app-redux/hooks'

import { SET_DECK_STATE } from 'app-redux/deck/actions/types'
import { Comment } from 'services/apiTypes/comment.types'

import CommentService from 'services/comment.service'
import ToastService from 'services/toast.service'

import Thread, { updateCommentChain, removeUserInputs } from 'components/comments/Thread'
import Editor from 'components/formElements/richText/Editor'
import LineLoader from 'components/elements/LineLoader'

import { emptyTextArea } from 'types/editor'

import styles from './commentContainer.module.scss'

type Props = {
  className?: string
  noHeader?: boolean
  header?: string
}

const CommentContainer = memo(({ className = '', header, noHeader = false }: Props) => {
  const [{ tbId: userId }] = useCookies(['tbId'])

  const commentId = useAppSelector(state => state.deck.commentid)
  const thread = useAppSelector(state => state.deck.thread)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [comment, setComment] = useState(emptyTextArea)

  const [setDeckState] = useActionless(SET_DECK_STATE)

  useEffect(() => {
    if (!commentId) return

    // If we've already loaded a thread into redux, don't re-fetch it
    if (thread) return setLoading(false)

    CommentService.details(commentId)
      .then(thread => setDeckState({ thread }))
      .catch(setError)
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (userId) return
    if (!thread) return

    const updatedThread = removeUserInputs(thread)

    setDeckState({ thread: updatedThread })
  }, [userId])

  const handleUpdateDeckThread = (updatedComment: Comment) => {
    if (!thread) return

    const updatedRootThread = updateCommentChain(thread, updatedComment)

    setDeckState({ thread: updatedRootThread })
  }

  const handleSubmitComment = () => {
    if (!thread) return Promise.resolve()

    return CommentService.createComment(thread.id, comment)
      .then(newComment => {
        const updatedComment: Comment = {
          ...thread,
          children: [newComment, ...thread.children],
          childrenCount: thread.childrenCount + 1,
        }

        setComment(emptyTextArea)
        setDeckState({ thread: updatedComment })
      })
      .catch(ToastService.create)
  }

  let content = null

  // Used when viewing a snapshot (there are no comments)
  if (!commentId) content = null
  else if (loading) content = <LineLoader />
  else if (error || !thread) content = <div className={styles.error}>Unable to load comments</div>
  else
    content = (
      // Rendering a thread with a custom child replaces the default root comment being rendered
      // This is useful for a deck where the root comment is always empty (auto generated per deck)
      <Thread
        rootPageComment
        noMarginOnFirstChildren
        noCollapse
        noPaginatorView
        comment={thread}
        commentClassName={styles.comment}
        onCommentUpdate={handleUpdateDeckThread}
        className={styles.container}
        archived={thread.archived}>
        {!noHeader && <h4 className={styles.header}>{header || 'Leave a comment'}</h4>}
        <Editor onChange={setComment} value={comment} placeholder="Leave a comment" onSubmit={handleSubmitComment} />
      </Thread>
    )

  return <div className={className}>{content}</div>
})

export default CommentContainer
